///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Cloud-top relief lid of the storm column: a flat grid over the block
/// footprint that the vertex shader displaces from the storm-centered IR
/// frame (see storm_column_lid.glsl). Frames swap by changing the two
/// textures and the blend, so scrubbing the clock never rebuilds geometry.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { type sphere } from "@stovokor/math/sphere"
import { vec2, vec4 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type StormCloudField } from "./StormCloudField"
import { boxHeightKm, type StormColumnLayout } from "./stormColumnLayout"

/** Grid vertices along the block's long axis */
const cGridAlong = 192

export class StormCloudLidNode extends NodeBase {
	private readonly lidRenderer: StormCloudLidRenderer
	private readonly lidContext: StovokorContext

	constructor(context: StovokorContext, layout: StormColumnLayout) {
		super()
		this.lidContext = context
		const geometry = new StormCloudLidGeometry(context, layout)
		this.lidRenderer = new StormCloudLidRenderer(context, geometry)
		this.lidRenderer.center.value = new vec2(layout.centerLat, layout.centerLon)
		this.lidRenderer.strike.value = layout.strike
		this.lidRenderer.heightScale.value = layout.heightScale
		this.lidRenderer.cellKm.value = new vec2(geometry.dx, geometry.dz)
		this.lidRenderer.halfExtent.value = new vec2(layout.lengthKm / 2, layout.widthKm / 2)
		this.lidRenderer.frameA.value = context.blankTexture
		this.lidRenderer.frameB.value = context.blankTexture
		this.renderer = this.lidRenderer
		// After the walls, sections and wind floor in the blended pass
		this.renderOrder = 2
	}

	/** Show frame `a`, crossfading toward `b` by `blend` (b may equal a) */
	public setFrames(a: StormCloudField, b: StormCloudField, blend: number): void {
		this.lidRenderer.frameA.value = a.getTexture(this.lidContext)
		this.lidRenderer.frameB.value = b.getTexture(this.lidContext)
		this.lidRenderer.boundsA.value = frameBounds(a)
		this.lidRenderer.boundsB.value = frameBounds(b)
		this.lidRenderer.blend.value = Math.min(1, Math.max(0, blend))
	}

	/** 0..1 rise animation */
	public set reveal(value: number) {
		this.lidRenderer.reveal.value = value
	}

	/** 0..1 fade multiplier */
	public set opacity(value: number) {
		this.lidRenderer.opacity = value
	}
}

function frameBounds(field: StormCloudField): vec4 {
	return new vec4(field.minLon, field.minLat, field.maxLon, field.maxLat)
}

class StormCloudLidRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public frameA = new UniformDynamic<Texture>()
	public frameB = new UniformDynamic<Texture>()
	public boundsA = new UniformDynamic<vec4>(new vec4(0, 0, 1, 1))
	public boundsB = new UniformDynamic<vec4>(new vec4(0, 0, 1, 1))
	public blend = new UniformDynamic<number>(0)
	public center = new UniformDynamic<vec2>(new vec2(0, 0))
	public strike = new UniformDynamic<number>(0)
	public heightScale = new UniformDynamic<number>(1)
	public reveal = new UniformDynamic<number>(0)
	public cellKm = new UniformDynamic<vec2>(new vec2(1, 1))
	public halfExtent = new UniformDynamic<vec2>(new vec2(1, 1))
	public lidAlpha = new UniformDynamic<number>(1)
	public opacity = 1

	constructor(context: StovokorContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("storm_column_lid"), geometry, [transparentMaterial])
		// Blended so the floor shows through from above, but writes depth so
		// it occludes the walls and columns behind it
		this.drawState.depthWrite = true
		this.drawState.cullFace = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		// Camera elevation over the block: solid edge-on, translucent from above
		const camLocal = state.modelMatrix.invert().multVec3(state.camera.position, 1)
		const len = camLocal.length
		const cosTilt = len > 0 ? Math.max(0, camLocal.y / len) : 1
		const t = smoothstep(0.2, 0.75, cosTilt)
		this.lidAlpha.value = (0.96 + (0.48 - 0.96) * t) * this.opacity
	}
}

function smoothstep(e0: number, e1: number, x: number): number {
	const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)))
	return t * t * (3 - 2 * t)
}

class StormCloudLidGeometry implements Geometry {
	public position: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true
	public readonly dx: number
	public readonly dz: number

	constructor(context: StovokorContext, layout: StormColumnLayout) {
		this.position = new VertexAttribute(context, 3)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position])

		const nx = cGridAlong
		const nz = Math.max(48, Math.round(cGridAlong * layout.widthKm / layout.lengthKm))
		this.dx = layout.lengthKm / (nx - 1)
		this.dz = layout.widthKm / (nz - 1)
		const halfX = layout.lengthKm / 2
		const halfZ = layout.widthKm / 2
		for (let j = 0; j < nz; j++) {
			const z = -halfZ + j * this.dz
			for (let i = 0; i < nx; i++) {
				this.position.addPos(-halfX + i * this.dx, 0, z)
			}
		}
		for (let j = 0; j < nz - 1; j++) {
			for (let i = 0; i < nx - 1; i++) {
				const a = j * nx + i
				const b = a + 1
				const c = a + nx + 1
				const d = a + nx
				this.indexBuffer.quad(a, d, c, b)
			}
		}

		// Bounds must cover the displaced lid for culling
		this.bounds = new cube()
		this.bounds.update(-halfX, 0, -halfZ)
		this.bounds.update(halfX, boxHeightKm(layout), halfZ)
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
